import { useState } from 'react'
import type { JSX } from 'react'

import {
  DownloadIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  Flex,
  IconButton,
  RefreshCwIcon,
  UpgradeBadge,
} from '@buffer-mono/popcorn'

import { ChannelDeveloperDataMenuItems } from '~publish/components/DeveloperData'

import { getTimeAgo } from '../../utils'

interface TakeawaysDeveloperData {
  takeawaysLoading: boolean
  takeawaysRefreshing: boolean
  onRefreshTakeaways: () => void
}

export type DeveloperDataProps =
  | (TakeawaysDeveloperData & {
      kind: 'channel'
      channelId: string
      serviceId?: string | null
    })
  | (TakeawaysDeveloperData & { kind: 'allChannels' })

interface InsightsDeveloperActionsMenuProps {
  developerData: DeveloperDataProps
}

export function InsightsDeveloperActionsMenu({
  developerData,
}: InsightsDeveloperActionsMenuProps): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton
          label="Developer options"
          tooltip="Developer options"
          size="small"
          variant="tertiary"
        >
          <EllipsisVerticalIcon size="xsmall" />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <InsightsDeveloperDataMenuItems developerData={developerData} />
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

interface InsightsActionsMenuProps {
  /** Link to the Analyze product. Omit to hide the "Go to Analyze" item. */
  analyzeUrl?: string
  /** From the channel analytics query. */
  channelLastUpdatedAt?: string | null
  postsLastUpdatedAt?: string | null
  /** Whether the export section should be shown. */
  showExport: boolean
  /** Whether to show upgrade-path UI (badges). */
  showUpgradePath: boolean
  /** True while an export job is in flight — disables the sub-trigger. */
  isExporting: boolean
  /** True once the export has passed the long-running threshold. */
  isLongRunning: boolean
  /** Trigger an export for the given format (or open the upgrade dialog). */
  onExportSelect: (format: 'csv' | 'markdown' | 'pdf') => void
  /** Client-side ZIP of the current Share-as-Post images. Not paywalled. */
  onDownloadSnapshots: () => void
  /** Whether any section currently has a capturable snapshot. */
  canDownloadSnapshots: boolean
  /** True while the snapshot ZIP is being built. */
  isPreparingSnapshots: boolean
  /** When provided, shows developer-only items. Omit to hide them. */
  developerData?: DeveloperDataProps
}

export function InsightsActionsMenu({
  analyzeUrl,
  channelLastUpdatedAt,
  postsLastUpdatedAt,
  showExport,
  showUpgradePath,
  isExporting,
  isLongRunning,
  onExportSelect,
  onDownloadSnapshots,
  canDownloadSnapshots,
  isPreparingSnapshots,
  developerData,
}: InsightsActionsMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)

  // Post and channel metrics are collected separately, so each reports its own
  // time, mirroring the sync popover the wide layout shows instead of this menu.
  // Only show a line when there's an actual timestamp: getTimeAgo returns
  // 'recently' for null/undefined, which would mislead users on channels that
  // have never synced.
  const postsTimeAgo = postsLastUpdatedAt
    ? getTimeAgo(postsLastUpdatedAt)
    : null
  const channelTimeAgo = channelLastUpdatedAt
    ? getTimeAgo(channelLastUpdatedAt)
    : null
  const hasTimestamps = Boolean(postsTimeAgo || channelTimeAgo)

  // Guard the developerData separator against being orphaned at the top.
  const hasPrecedingContent = Boolean(analyzeUrl || showExport || hasTimestamps)

  const handleExportSelect = (format: 'csv' | 'markdown' | 'pdf'): void => {
    onExportSelect(format)
    setIsOpen(false)
  }

  const handleDownloadSnapshots = (): void => {
    onDownloadSnapshots()
    setIsOpen(false)
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
        <DropdownMenu.Trigger>
          <IconButton
            variant="tertiary"
            label="More actions"
            tooltip="More actions"
            size="small"
          >
            <EllipsisVerticalIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          {analyzeUrl && (
            <>
              <DropdownMenu.Item asChild>
                <a href={analyzeUrl} target="_blank" rel="noopener noreferrer">
                  Go to Analyze
                </a>
              </DropdownMenu.Item>
              {showExport && <DropdownMenu.Separator />}
            </>
          )}
          {showExport && (
            <DropdownMenu.Sub>
              {/* Disabled while an export is in-flight to prevent a second
                  call overwriting the shared localStorage job ID, and while a
                  snapshot ZIP is being built. */}
              <DropdownMenu.SubTrigger
                disabled={isExporting || isPreparingSnapshots}
              >
                <DownloadIcon />
                {isPreparingSnapshots
                  ? 'Preparing images…'
                  : isExporting
                    ? isLongRunning
                      ? "Exporting… it'll download automatically"
                      : 'Exporting…'
                    : 'Export'}
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubMenu>
                <DropdownMenu.Item onSelect={() => handleExportSelect('csv')}>
                  <Flex align="center" gap="xs">
                    Export as CSV
                    {showUpgradePath && (
                      <UpgradeBadge size="small" aria-hidden="true" />
                    )}
                  </Flex>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => handleExportSelect('markdown')}
                >
                  <Flex align="center" gap="xs">
                    Export as Markdown
                    {showUpgradePath && (
                      <UpgradeBadge size="small" aria-hidden="true" />
                    )}
                  </Flex>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => handleExportSelect('pdf')}>
                  <Flex align="center" gap="xs">
                    Export as PDF
                    {showUpgradePath && (
                      <UpgradeBadge size="small" aria-hidden="true" />
                    )}
                  </Flex>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  disabled={!canDownloadSnapshots}
                  onSelect={handleDownloadSnapshots}
                >
                  Download Snapshots
                </DropdownMenu.Item>
              </DropdownMenu.SubMenu>
            </DropdownMenu.Sub>
          )}
          {hasTimestamps && (
            <>
              <DropdownMenu.Separator />
              {postsTimeAgo && (
                <DropdownMenu.Label>
                  Post metrics updated {postsTimeAgo}
                </DropdownMenu.Label>
              )}
              {channelTimeAgo && (
                <DropdownMenu.Label>
                  Channel metrics updated {channelTimeAgo}
                </DropdownMenu.Label>
              )}
            </>
          )}
          {developerData && (
            <>
              {hasPrecedingContent && <DropdownMenu.Separator />}
              <InsightsDeveloperDataMenuItems developerData={developerData} />
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
    </>
  )
}

function InsightsDeveloperDataMenuItems({
  developerData,
}: InsightsDeveloperActionsMenuProps): JSX.Element {
  return (
    <>
      {developerData.kind === 'channel' ? (
        <ChannelDeveloperDataMenuItems
          channelId={developerData.channelId}
          serviceId={developerData.serviceId ?? undefined}
          showBackfillAnalytics
        />
      ) : (
        <DropdownMenu.Label>Developer Data</DropdownMenu.Label>
      )}
      <DropdownMenu.Item
        loading={developerData.takeawaysRefreshing}
        disabled={
          developerData.takeawaysLoading || developerData.takeawaysRefreshing
        }
        onSelect={developerData.onRefreshTakeaways}
      >
        <RefreshCwIcon />
        Refresh Takeaways
      </DropdownMenu.Item>
    </>
  )
}
